import { cva, type VariantProps } from 'class-variance-authority'
import { CheckboxButton, CheckboxField, type CheckboxFieldProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Checkbox}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @today/tokens
 * (resolved per bound brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-4 w-4), md (h-5 w-5, default), lg (h-6 w-6)
 * - Focus ring uses the bound brand's primary colour
 * - Checked state uses the bound brand's primary background
 *
 * @example
 * ```tsx
 * const classes = checkboxVariants({ size: 'md' })
 * // Returns: "h-5 w-5 rounded-ds-sm border ..."
 * ```
 */
export const checkboxVariants = cva(
	'rounded-ds-sm border-2 border-ds-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&[data-selected]]:bg-ds-primary [&[data-selected]]:border-ds-primary [&[data-selected]]:text-ds-on-primary',
	{
		variants: {
			size: {
				sm: 'h-4 w-4',
				md: 'h-5 w-5',
				lg: 'h-6 w-6',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Checkbox} component.
 *
 * Extends React Aria's CheckboxFieldProps with CVA size variants.
 *
 * @property size - Checkbox size: 'sm' (h-4 w-4) | 'md' (h-5 w-5, default) | 'lg' (h-6 w-6)
 * @property className - Optional additional Tailwind classes
 * @property children - Label text
 * @property isDisabled - Disabled state (React Aria pattern)
 * @property isIndeterminate - Indeterminate state (tri-state checkbox)
 * @property isSelected - Controlled checked state
 *
 * @example
 * ```tsx
 * <Checkbox size="md" isSelected={true}>
 *   Accept terms
 * </Checkbox>
 * ```
 */
export interface CheckboxProps extends Omit<CheckboxFieldProps, 'className'>, VariantProps<typeof checkboxVariants> {
	className?: string
}

/**
 * Accessible checkbox primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation (Space to toggle), focus management,
 *   and ARIA attributes provided by React Aria's `<CheckboxField>` + `<CheckboxButton>`.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@today/tokens`
 *   (resolved per bound brand). No hardcoded values.
 * - **Sizes:** Small (h-4 w-4), medium (h-5 w-5, default), large (h-6 w-6).
 * - **Indeterminate:** Supports tri-state (unchecked, checked, indeterminate).
 * - **Focus indicator:** 2px ring in the bound brand's primary colour.
 * - **Checked state:** primary background.
 *
 * @example Basic usage
 * ```tsx
 * import { Checkbox } from '@today/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Checkbox>Accept terms and conditions</Checkbox>
 *       <Checkbox isRequired>I agree to the privacy policy</Checkbox>
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With controlled state
 * ```tsx
 * const [isChecked, setIsChecked] = useState(false)
 *
 * <Checkbox
 *   isSelected={isChecked}
 *   onChange={setIsChecked}
 * >
 *   Subscribe to newsletter
 * </Checkbox>
 * ```
 *
 * @example Indeterminate state
 * ```tsx
 * <Checkbox isIndeterminate>
 *   Select all (some selected)
 * </Checkbox>
 * ```
 *
 * @see {@link checkboxVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/checkbox/checkbox.stories.tsx for visual examples
 */
export function Checkbox({ size, className, children, ...props }: CheckboxProps) {
	return (
		<CheckboxField {...props}>
			<CheckboxButton className="flex items-center gap-2">
				<div className={cn(checkboxVariants({ size }), className)}>
					<svg
						className="h-full w-full stroke-current"
						viewBox="0 0 24 24"
						fill="none"
						strokeWidth="3"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						{props.isIndeterminate ? (
							<line x1="6" y1="12" x2="18" y2="12" />
						) : (
							<polyline points="20 6 9 17 4 12" className="opacity-0 [.group[data-selected]_&]:opacity-100" />
						)}
					</svg>
				</div>
				{children && <span className="text-ds-base text-ds-text select-none">{children as React.ReactNode}</span>}
			</CheckboxButton>
		</CheckboxField>
	)
}
